import { getToken } from ".";
import { HttpException } from "../../models/exceptions";
import { User } from "../../models/user";

// ### Fetching users for a company
export async function fetchCompanyUsers({
  companyName,
  signal,
  colibry,
  active,
  email,
}: {
  companyName: string;
  signal?: any;
  colibry?: boolean;
  active?: boolean;
  email?: string;
}): Promise<User[]> {
  const token = await getToken();

  const queryParams = new URLSearchParams();
  if (colibry !== undefined) queryParams.append("colibry", String(colibry));
  if (active !== undefined) queryParams.append("active", String(active));
  if (email) queryParams.append("email", email);

  const url = `${(window as any)._env_.REACT_APP_API_HOST}/companies/${companyName}/users${
    queryParams.toString() ? `?${queryParams.toString()}` : ""
  }`;

  const response = await fetch(url, {
    signal: signal,
    headers: {
      Authorization: "Bearer " + token,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const error = new HttpException("An error occurred while fetching the company users.");
    error.code = response.status;
    error.info = await response.json();
    throw error;
  }

  const users = await response.json();
  return users;
}

// ### Searching users globally by email (partial, case-insensitive)
// Results are scoped by the backend to the requesting user's company access.
export async function searchUsers({ email, signal }: { email: string; signal?: any }): Promise<User[]> {
  const token = await getToken();

  const queryParams = new URLSearchParams();
  queryParams.append("email", email);
  queryParams.append("size", "10");

  const url = `${(window as any)._env_.REACT_APP_API_HOST}/users?${queryParams.toString()}`;

  const response = await fetch(url, {
    signal: signal,
    headers: {
      Authorization: "Bearer " + token,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const error = new HttpException("An error occurred while searching users.");
    error.code = response.status;
    error.info = await response.json();
    throw error;
  }

  const page = await response.json();
  return page.items ?? [];
}

// ### Fetching a single user by ID
export async function fetchUser({ userId, signal }: { userId: string; signal?: any }): Promise<User> {
  const token = await getToken();

  const url = `${(window as any)._env_.REACT_APP_API_HOST}/users/${userId}`;

  const response = await fetch(url, {
    signal: signal,
    headers: {
      Authorization: "Bearer " + token,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const error = new HttpException("An error occurred while fetching the user.");
    error.code = response.status;
    error.info = await response.json();
    throw error;
  }

  const user = await response.json();
  return user;
}

// ### Updating a user
export async function patchUser({ userId, patch }: { userId: string; patch: any }): Promise<User> {
  const token = await getToken();

  const url = `${(window as any)._env_.REACT_APP_API_HOST}/users/${userId}`;

  const response = await fetch(url, {
    method: "PATCH",
    headers: {
      Authorization: "Bearer " + token,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(patch),
  });

  if (!response.ok) {
    const error = new HttpException("An error occurred while updating the user.");
    error.code = response.status;
    error.info = await response.json();
    throw error;
  }

  const user = await response.json();
  return user;
}

// ### Creating a new user for a company
export async function putCompanyUser({
  companyNameOrId,
  email,
  displayName,
}: {
  companyNameOrId: string;
  email: string;
  displayName: string;
}): Promise<{ id: string }> {
  const token = await getToken();

  const url = `${(window as any)._env_.REACT_APP_API_HOST}/companies/${companyNameOrId}/users`;

  const response = await fetch(url, {
    method: "PUT",
    headers: {
      Authorization: "Bearer " + token,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      email,
      display_name: displayName,
    }),
  });

  if (!response.ok) {
    const error = new HttpException("An error occurred while creating the user.");
    error.code = response.status;
    error.info = await response.json();
    throw error;
  }

  const result = await response.json();
  return result;
}

// ### Fetching current user info
export async function fetchMe({ token }: { token: string }): Promise<User> {
  const url = `${(window as any)._env_.REACT_APP_API_HOST}/me`;

  const response = await fetch(url, {
    headers: {
      Authorization: "Bearer " + token,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const error = new HttpException("An error occurred while fetching current user info.");
    error.code = response.status;
    error.info = await response.json();
    throw error;
  }

  const user = await response.json();
  return user;
}

// ### Requesting a passwordless magic-link sign-in email
// Public endpoint (no auth): the backend always responds 200 regardless of
// whether the email is allowed, to avoid leaking membership information.
export async function requestMagicLink({ email }: { email: string }): Promise<void> {
  const url = `${(window as any)._env_.REACT_APP_API_HOST}/users/magic_link`;

  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ email, platform: "watchtower" }),
  });

  if (!response.ok) {
    const error = new HttpException("An error occurred while requesting the magic link.");
    error.code = response.status;
    error.info = await response.json();
    throw error;
  }
}
